/* eslint-disable */
import { useCallback, useMemo } from 'react';
import useDebouncedState from '@root/lib/hooks/UseDebouncedState';
import usePostEffect from '@root/lib/hooks/usePostEffect';
import Increment from '@root/lib/components/Icons/Increment';
import Decrement from '@root/lib/components/Icons/Decrement';
import Image from '@root/lib/components/Image';
import './LineItem.scss';

/**
 *
 * @param {{
 *   unitPrice: number;
 *   quantity: number;
 *   title: string;
 *   linePrice: number;
 *   disabled?: boolean;
 *   variant: {
 *     price: number;
 *     images: {
 *       alt?: string;
 *       thumbnail: string;
 *     };
 *     product: {
 *       id: number;
 *     };
 *     title: string;
 *   };
 *   onQuantityChange: (qty: number) => Promise<void>;
 * }} props
 */
const LineItem = ({
  quantity,
  title,
  variant,
  discounts,
  linePrice,
  disabled,
  onQuantityChange,
}) => {
  const [ localQty, debouncedLocalQty, setLocalQty, setLocalQtyI ] = useDebouncedState(quantity);
  const adjustedPrice = useMemo(() => {
    return discounts.reduce((price, d) => {
      if (d.discountApplication.allocationMethod !== 'EACH') return price;
      return price - d.allocatedAmount;
    }, linePrice);
  }, [discounts, linePrice]);

  const variantTitle = variant.title === 'Default Title' ? null : variant.title?.split('-')[0].trim();
  const shownTitle = (() => {
    let t = title;
    if (variantTitle && variantTitle !== 'Default Title') t = `(${variantTitle}) ${t}`;
    return t;
  })();

  /**
   * Increases the local quantity by 1
   */
  const incrementQuantity = useCallback(() => {
    setLocalQty(qty => qty + 1);
  }, [setLocalQty]);

  const decrementQuantity = useCallback(() => {
    setLocalQty(qty => Math.max(0, qty - 1));
  }, [setLocalQty]);

  // Calling onQuantityChange when the debounced value changes. quantity is used in this effect
  // but is purposfully left out of the deps so as to not trigger this function when the parent
  // changes the quantity.
  usePostEffect(() => {
    if (quantity === debouncedLocalQty) return;
    onQuantityChange(debouncedLocalQty);
  }, [debouncedLocalQty]);

  // Forcing update of local quantity when prop value changes
  usePostEffect(() => {
    setLocalQtyI(quantity);
  }, [quantity]);

  return (
    <div className="LineItem">
      <div className="LineItem__ImageWrapper">
        <Image className="LineItem__Image" src={variant.images.thumbnail} alt={title} ratio="1:1" />
      </div>
      <span className="LineItem__Title">{shownTitle}</span>
      <div className="LineItem__QtyCtrl">
        <button className="LineItem__QtySub" onClick={decrementQuantity} disabled={disabled}>
          <Decrement />
        </button>
        <span className="LineItem__Qty">{localQty}</span>
        <button className="LineItem__QtyAdd" onClick={incrementQuantity} disabled={disabled}>
          <Increment />
        </button>
      </div>
      <span className="LineItem__Price">
        {adjustedPrice !== linePrice && (
          <span className="LineItem__AdjustedPrice">{Shopify.formatMoney(adjustedPrice)}</span>
        )}
        <span className="LineItem__OriginalPrice">{Shopify.formatMoney(linePrice)}</span>
      </span>
    </div>
  );
};

export default LineItem;
